import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  ArrowBackOutlined as BackIcon,
  ArrowDownwardOutlined as DownIcon,
  ArrowUpwardOutlined as UpIcon,
  AutoAwesomeOutlined as OptimizeIcon,
  DeleteOutline as DeleteIcon,
  LabelOutlined as LabelIcon,
  LocationOnOutlined as StopsIcon,
  MapOutlined as DistanceIcon,
  SaveOutlined as SaveIcon,
  Search as SearchIcon,
  ScheduleOutlined as TimeIcon,
} from '@mui/icons-material';
import LabelChips from '../../components/common/LabelChips';
import RouteBuilderMap from '../../components/transportation/RouteBuilderMap';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import { SAUDI_CITIES } from '../../constants/saudiCities';
import { APP_SHAPE } from '../../constants/shape';
import { useRoutedPath } from '../../hooks/useRoutedPath';
import { listCollectionPoints } from '../../services/collection-point.service';
import { listFacilities } from '../../services/facility.service';
import { listSuppliers } from '../../services/supplier.service';
import { getAllWasteCatalogMaterials } from '../../services/waste-catalog.service';
import { listZones } from '../../services/zone.service';
import { useOrganizationStore } from '../../stores/organization.store';
import { useRouteStore } from '../../stores/route.store';
import type { Facility } from '../../types/facility.type';
import type { RouteInput, RouteStop } from '../../types/route.type';
import type { Supplier, SupplierCollectionPoint } from '../../types/supplier.type';
import type { Zone } from '../../types/zone.type';
import { getRouteColor } from '../../utils/routeColors';
import { computeRouteMetrics, formatEta, optimizeStopOrder } from '../../utils/routeSuggestions';

function CompactMetric({ icon, value }: { icon: ReactNode; value: ReactNode }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ color: 'text.secondary', display: 'flex', '& svg': { fontSize: 19 } }}>{icon}</Box>
      <Typography variant="body1" fontWeight={600}>{value}</Typography>
    </Stack>
  );
}

export default function RouteBuilder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { routeId } = useParams<{ routeId: string }>();
  const isEditing = Boolean(routeId);
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const { selectedRoute, selectedRouteNotFound, isLoading, isSaving, error, clearError, fetchRoute, createRoute, updateRoute } = useRouteStore();

  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [startFacilityId, setStartFacilityId] = useState('');
  const [endFacilityId, setEndFacilityId] = useState('');
  // One entry per planned stop. The same point may appear once per waste type it
  // produces; materialId null means the stop collects whatever the point inherits.
  const [selectedStops, setSelectedStops] = useState<RouteStop[]>([]);
  const [materialPicker, setMaterialPicker] = useState<{ point: SupplierCollectionPoint; options: string[] } | null>(null);
  const [materialLabelsById, setMaterialLabelsById] = useState<Map<string, string>>(new Map());
  const [submitted, setSubmitted] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [tab, setTab] = useState<'stops' | 'add'>('stops');
  const [optimizing, setOptimizing] = useState(false);
  const [optimizeError, setOptimizeError] = useState<string | null>(null);
  const [optimizeBaseline, setOptimizeBaseline] = useState<{ distanceKm: number; etaMinutes: number } | null>(null);
  const [optimizeImprovement, setOptimizeImprovement] = useState<{ distancePct: number; timePct: number } | null>(null);
  const sawRoutingReloadSinceOptimize = useRef(false);

  const [allPoints, setAllPoints] = useState<SupplierCollectionPoint[]>([]);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState<Supplier[]>([]);
  const [zoneOptions, setZoneOptions] = useState<Zone[]>([]);
  const [facilityOptions, setFacilityOptions] = useState<Facility[]>([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [facilitiesLoadFailed, setFacilitiesLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [groupBy, setGroupBy] = useState<'none' | 'city' | 'supplier'>('none');

  const orgId = selectedOrganization?.id;

  useEffect(() => {
    if (orgId && routeId) void fetchRoute(orgId, routeId);
  }, [orgId, routeId, fetchRoute]);

  const loadFacilityOptions = useCallback(() => {
    if (!orgId) return;
    setFacilitiesLoadFailed(false);
    setFacilitiesLoading(true);
    void listFacilities({ organization_id: orgId, limit: 100 })
      .then((result) => setFacilityOptions(result.facilities))
      .catch(() => {
        setFacilityOptions([]);
        setFacilitiesLoadFailed(true);
      })
      .finally(() => setFacilitiesLoading(false));
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    setPointsLoading(true);
    void listCollectionPoints({ organization_id: orgId, limit: 500 })
      .then((result) => setAllPoints(result.collection_points))
      .catch(() => setAllPoints([]))
      .finally(() => setPointsLoading(false));
    void listSuppliers({ organization_id: orgId, limit: 500 }).then((result) => setSupplierOptions(result.suppliers)).catch(() => setSupplierOptions([]));
    void getAllWasteCatalogMaterials()
      .then((options) => setMaterialLabelsById(new Map(options.map((option) => [option.value, option.label]))))
      .catch(() => setMaterialLabelsById(new Map()));
    void listZones({ organization_id: orgId, limit: 100 }).then((result) => setZoneOptions(result.zones)).catch(() => setZoneOptions([]));
    loadFacilityOptions();
  }, [orgId, loadFacilityOptions]);

  useEffect(() => {
    if (initialized) return;
    if (isEditing) {
      if (!selectedRoute || selectedRoute.id !== routeId) return;
      setName(selectedRoute.name);
      setCity(selectedRoute.city);
      setSelectedStops(
        selectedRoute.stops?.length
          ? selectedRoute.stops
          : selectedRoute.collection_point_ids.map((id) => ({ collection_point_id: id, material_id: null })),
      );
      setStartFacilityId(selectedRoute.start_facility_id ?? '');
      setEndFacilityId(selectedRoute.end_facility_id ?? '');
      setInitialized(true);
    } else {
      setInitialized(true);
    }
  }, [isEditing, selectedRoute, routeId, initialized]);

  const pointById = useMemo(() => new Map(allPoints.map((point) => [point.id, point])), [allPoints]);
  const supplierNameById = useMemo(() => new Map(supplierOptions.map((supplier) => [supplier.id, supplier.name])), [supplierOptions]);
  const supplierById = useMemo(() => new Map(supplierOptions.map((supplier) => [supplier.id, supplier])), [supplierOptions]);

  // The waste types a point accepts, inherited: its own narrowing wins, else its supplier's set.
  const inheritedMaterialIds = useCallback(
    (point: SupplierCollectionPoint): string[] =>
      point.material_ids?.length ? point.material_ids : (supplierById.get(point.supplier_id)?.material_ids ?? []),
    [supplierById],
  );

  const usedMaterialsByPoint = useMemo(() => {
    const used = new Map<string, Set<string | null>>();
    for (const stop of selectedStops) {
      const existing = used.get(stop.collection_point_id) ?? new Set<string | null>();
      existing.add(stop.material_id);
      used.set(stop.collection_point_id, existing);
    }
    return used;
  }, [selectedStops]);

  const remainingMaterials = useCallback(
    (point: SupplierCollectionPoint): string[] => {
      const used = usedMaterialsByPoint.get(point.id);
      return inheritedMaterialIds(point).filter((id) => !used?.has(id));
    },
    [usedMaterialsByPoint, inheritedMaterialIds],
  );

  const selectedPoints = useMemo(
    () => selectedStops.map((stop) => pointById.get(stop.collection_point_id)).filter((point): point is SupplierCollectionPoint => Boolean(point)),
    [selectedStops, pointById],
  );

  // Every marker once, whatever the number of planned stops at that point.
  const uniqueSelectedPoints = useMemo(() => {
    const seen = new Set<string>();
    return selectedPoints.filter((point) => (seen.has(point.id) ? false : (seen.add(point.id), true)));
  }, [selectedPoints]);

  // A point stays addable while it has waste types not yet planned.
  const availablePoints = useMemo(
    () =>
      allPoints.filter((point) => {
        const used = usedMaterialsByPoint.get(point.id);
        if (!used || used.size === 0) return true;
        return remainingMaterials(point).length > 0;
      }),
    [allPoints, usedMaterialsByPoint, remainingMaterials],
  );

  const startFacility = useMemo(() => facilityOptions.find((facility) => facility.id === startFacilityId) ?? null, [facilityOptions, startFacilityId]);
  const endFacility = useMemo(() => facilityOptions.find((facility) => facility.id === endFacilityId) ?? null, [facilityOptions, endFacilityId]);

  const filteredAvailable = useMemo(() => {
    const term = search.trim().toLowerCase();
    return availablePoints.filter((point) => {
      if (supplierFilter && point.supplier_id !== supplierFilter) return false;
      if (term && !point.name.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [availablePoints, search, supplierFilter]);

  const groupedAvailable = useMemo(() => {
    if (groupBy === 'none') return filteredAvailable.map((point) => ({ point, groupLabel: null as string | null }));
    const keyFor = (point: SupplierCollectionPoint) =>
      groupBy === 'city' ? (point.city || t('suppliers.collectionPoints.list.noCity')) : (supplierNameById.get(point.supplier_id) ?? t('suppliers.collectionPoints.list.noCity'));
    const sorted = [...filteredAvailable].sort((a, b) => keyFor(a).localeCompare(keyFor(b)));
    let previousKey: string | null = null;
    return sorted.map((point) => {
      const key = keyFor(point);
      const groupLabel = key === previousKey ? null : key;
      previousKey = key;
      return { point, groupLabel };
    });
  }, [filteredAvailable, groupBy, supplierNameById, t]);

  const routed = useRoutedPath(selectedPoints, startFacility, endFacility);
  const fallbackMetrics = useMemo(
    () => computeRouteMetrics(selectedPoints, startFacility, endFacility),
    [selectedPoints, startFacility, endFacility],
  );
  const distanceKm = routed.isRouted ? routed.distanceKm! : fallbackMetrics.distanceKm;
  const etaMinutes = routed.isRouted ? routed.durationMinutes! : fallbackMetrics.etaMinutes;

  // After an optimize reorders the stops, useRoutedPath re-fetches for the new
  // order. Wait for a full loading cycle (true, then false) before comparing
  // against the pre-optimize baseline - otherwise we'd momentarily compare the
  // old routed result against the new order's not-yet-settled fallback metric.
  useEffect(() => {
    if (!optimizeBaseline) return;
    if (routed.loading) {
      sawRoutingReloadSinceOptimize.current = true;
      return;
    }
    if (!sawRoutingReloadSinceOptimize.current) return;
    sawRoutingReloadSinceOptimize.current = false;
    const before = optimizeBaseline;
    setOptimizeBaseline(null);
    if (before.distanceKm <= 0) return;
    setOptimizeImprovement({
      distancePct: Math.round(((before.distanceKm - distanceKm) / before.distanceKm) * 100),
      timePct: before.etaMinutes > 0 ? Math.round(((before.etaMinutes - etaMinutes) / before.etaMinutes) * 100) : 0,
    });
  }, [routed.loading, optimizeBaseline, distanceKm, etaMinutes]);

  const isValid = name.trim().length > 0 && city.trim().length > 0 && selectedStops.length > 0 && Boolean(startFacilityId) && Boolean(endFacilityId);

  if (!selectedOrganization) return <Alert severity="info" sx={{ m: 2 }}>{t('transportation.routes.selectOrganization')}</Alert>;

  if (isEditing && isLoading && !initialized) {
    return <Box sx={{ p: 2 }}><LoadingSkeleton variant="cards" filters={false} cardCount={4} /></Box>;
  }

  if (isEditing && selectedRouteNotFound) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{t('transportation.routes.details.loadError')}</Alert>
      </Box>
    );
  }

  const pushStop = (pointId: string, materialId: string | null) =>
    setSelectedStops((current) =>
      current.some((stop) => stop.collection_point_id === pointId && stop.material_id === materialId)
        ? current
        : [...current, { collection_point_id: pointId, material_id: materialId }],
    );
  const addPoint = (id: string) => {
    const point = pointById.get(id);
    if (!point) return;
    const inherited = inheritedMaterialIds(point);
    if (inherited.length === 0) {
      pushStop(point.id, null);
      return;
    }
    const remaining = remainingMaterials(point);
    if (remaining.length === 0) return;
    if (remaining.length === 1) {
      pushStop(point.id, remaining[0]);
      return;
    }
    // More than one type left to plan -- the waste type must be chosen.
    setMaterialPicker({ point, options: remaining });
  };
  const removePoint = (id: string) => setSelectedStops((current) => current.filter((stop) => stop.collection_point_id !== id));
  const removeStopAt = (index: number) => setSelectedStops((current) => current.filter((_, position) => position !== index));
  const toggle = (id: string) => (selectedStops.some((stop) => stop.collection_point_id === id) ? removePoint(id) : addPoint(id));
  const movePoint = (index: number, direction: -1 | 1) => {
    setSelectedStops((current) => {
      const next = [...current];
      const target = index + direction;
      if (target < 0 || target >= next.length) return current;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };
  const selectByLabel = (zoneId: string) => {
    const supplierIdsInLabel = new Set(
      allPoints.filter((point) => point.labels?.some((label) => label.id === zoneId)).map((point) => point.supplier_id),
    );
    if (supplierIdsInLabel.size === 0) return;
    const pointsInLabel = allPoints.filter((point) => supplierIdsInLabel.has(point.supplier_id));
    setSelectedStops((current) => {
      const next = [...current];
      const has = (pointId: string, materialId: string | null) =>
        next.some((stop) => stop.collection_point_id === pointId && stop.material_id === materialId);
      for (const point of pointsInLabel) {
        const inherited = inheritedMaterialIds(point);
        if (inherited.length === 0) {
          if (!next.some((stop) => stop.collection_point_id === point.id)) next.push({ collection_point_id: point.id, material_id: null });
        } else {
          for (const materialId of inherited) {
            if (!has(point.id, materialId)) next.push({ collection_point_id: point.id, material_id: materialId });
          }
        }
      }
      return next;
    });
  };

  const optimizeOrder = async () => {
    setOptimizing(true);
    setOptimizeError(null);
    setOptimizeImprovement(null);
    const baseline = { distanceKm, etaMinutes };
    const optimizedIds = await optimizeStopOrder(uniqueSelectedPoints, startFacility, endFacility);
    setOptimizing(false);
    if (optimizedIds) {
      sawRoutingReloadSinceOptimize.current = false;
      setOptimizeBaseline(baseline);
      const order = new Map(optimizedIds.map((id, index) => [id, index]));
      // Stable sort keeps a point's stops adjacent -- one physical visit, several types.
      setSelectedStops((current) =>
        [...current].sort(
          (a, b) => (order.get(a.collection_point_id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.collection_point_id) ?? Number.MAX_SAFE_INTEGER),
        ),
      );
    } else {
      setOptimizeError(t('transportation.routeBuilder.stops.optimizeError'));
    }
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (!isValid) return;
    const input: RouteInput = {
      name: name.trim(),
      city,
      collection_point_ids: selectedStops.map((stop) => stop.collection_point_id),
      stops: selectedStops,
      start_facility_id: startFacilityId,
      end_facility_id: endFacilityId,
    };
    const result = isEditing && routeId
      ? await updateRoute(selectedOrganization.id, routeId, input)
      : await createRoute(selectedOrganization.id, input);
    if (result) navigate(`/transportation/routes/${result.id}`);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <IconButton onClick={() => navigate('/transportation/routes')} aria-label={t('transportation.routes.details.backLabel')}>
            <BackIcon />
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {isEditing ? t('transportation.routeBuilder.editTitle') : t('transportation.routeBuilder.createTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
              {t('transportation.routeBuilder.subtitle')}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0 }}>
          <Button onClick={() => navigate('/transportation/routes')} disabled={isSaving}>{t('transportation.routes.form.cancel')}</Button>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
            onClick={() => void handleSave()}
            disabled={isSaving}
          >
            {isSaving ? t('transportation.routes.form.saving') : isEditing ? t('transportation.routes.form.save') : t('transportation.routes.form.create')}
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" onClose={clearError} sx={{ mx: 2, mt: 2 }}>{error}</Alert>}
      {submitted && selectedStops.length === 0 && <Alert severity="error" sx={{ mx: 2, mt: 2 }}>{t('transportation.routes.form.stopsError')}</Alert>}
      {submitted && (!startFacilityId || !endFacilityId) && <Alert severity="error" sx={{ mx: 2, mt: 2 }}>{t('transportation.routes.form.facilitiesError')}</Alert>}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', gap: 2, p: 2, flexDirection: { xs: 'column', lg: 'row' } }}>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: { xs: 360, lg: 0 } }}>
          <RouteBuilderMap
            availablePoints={filteredAvailable}
            selectedPoints={uniqueSelectedPoints}
            onToggle={toggle}
            startFacility={startFacility}
            endFacility={endFacility}
            routedGeometry={routed.geometry}
            routeLoading={routed.loading}
            routeFailed={routed.failed}
            color={routeId ? getRouteColor(routeId) : undefined}
            height="100%"
          />
        </Box>

        <Paper variant="outlined" sx={{ width: { xs: '100%', lg: 420 }, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0, borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                label={t('transportation.routes.form.name')}
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                error={submitted && !name.trim()}
              />
              <FormControl size="small" sx={{ minWidth: 150 }} required error={submitted && !city.trim()}>
                <InputLabel id="route-builder-city-label">{t('transportation.routes.form.city')}</InputLabel>
                <Select labelId="route-builder-city-label" label={t('transportation.routes.form.city')} value={city} onChange={(event) => setCity(event.target.value)}>
                  {SAUDI_CITIES.map((cityName) => <MenuItem key={cityName} value={cityName}>{cityName}</MenuItem>)}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
              <FormControl size="small" fullWidth required error={submitted && !startFacilityId}>
                <InputLabel id="route-builder-start-facility-label">{t('transportation.routes.form.startFacility')}</InputLabel>
                <Select
                  labelId="route-builder-start-facility-label"
                  label={t('transportation.routes.form.startFacility')}
                  value={startFacilityId}
                  onChange={(event) => setStartFacilityId(event.target.value)}
                >
                  {facilityOptions.map((facility) => (
                    <MenuItem key={facility.id} value={facility.id}>{`${facility.name} (${t(`facilities.type.${facility.type}`)})`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth required error={submitted && !endFacilityId}>
                <InputLabel id="route-builder-end-facility-label">{t('transportation.routes.form.endFacility')}</InputLabel>
                <Select
                  labelId="route-builder-end-facility-label"
                  label={t('transportation.routes.form.endFacility')}
                  value={endFacilityId}
                  onChange={(event) => setEndFacilityId(event.target.value)}
                >
                  {facilityOptions.map((facility) => (
                    <MenuItem key={facility.id} value={facility.id}>{`${facility.name} (${t(`facilities.type.${facility.type}`)})`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {facilitiesLoadFailed ? (
              <Alert
                severity="error"
                sx={{ mt: 1.5 }}
                action={(
                  <Button color="inherit" size="small" onClick={loadFacilityOptions}>
                    {t('suppliers.details.retry')}
                  </Button>
                )}
              >
                {t('transportation.routeBuilder.facilities.loadError')}
              </Alert>
            ) : !facilitiesLoading && facilityOptions.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {t('transportation.routeBuilder.facilities.empty')}{' '}
                <Link component={RouterLink} to="/transportation/facilities" underline="hover">
                  {t('transportation.routeBuilder.facilities.link')}
                </Link>
              </Typography>
            ) : null}
          </Box>

          <Divider />
          <Box sx={{ px: 2, py: 1.75 }}>
            <Stack direction="row" spacing={2.5} divider={<Divider orientation="vertical" flexItem />} justifyContent="center">
              <CompactMetric icon={<StopsIcon />} value={t('transportation.routeBuilder.metrics.stopsValue', { count: selectedStops.length })} />
              <CompactMetric icon={<DistanceIcon />} value={`${distanceKm} km`} />
              <CompactMetric icon={<TimeIcon />} value={formatEta(etaMinutes)} />
            </Stack>
          </Box>

          <Divider />
          <Tabs value={tab} onChange={(_, next) => setTab(next)} variant="fullWidth">
            <Tab value="stops" label={t('transportation.routeBuilder.stops.tabLabel', { count: selectedStops.length })} />
            <Tab value="add" label={t('transportation.routeBuilder.picker.title')} />
          </Tabs>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {tab === 'stops' ? (
              selectedPoints.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{t('transportation.routeBuilder.stops.empty')}</Typography>
              ) : (
                <>
                  {selectedPoints.length >= 3 && (
                    <Box sx={{ px: 2, pt: 1.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={optimizing ? <CircularProgress size={14} /> : <OptimizeIcon fontSize="small" />}
                        onClick={() => void optimizeOrder()}
                        disabled={optimizing}
                      >
                        {t('transportation.routeBuilder.stops.optimize')}
                      </Button>
                      {optimizeError && <Alert severity="warning" sx={{ mt: 1 }} onClose={() => setOptimizeError(null)}>{optimizeError}</Alert>}
                      {optimizeImprovement && (
                        optimizeImprovement.distancePct > 0 || optimizeImprovement.timePct > 0 ? (
                          <Alert severity="success" sx={{ mt: 1 }} onClose={() => setOptimizeImprovement(null)}>
                            {t('transportation.routeBuilder.stops.optimizeImproved', {
                              distancePct: Math.max(0, optimizeImprovement.distancePct),
                              timePct: Math.max(0, optimizeImprovement.timePct),
                            })}
                          </Alert>
                        ) : (
                          <Alert severity="info" sx={{ mt: 1 }} onClose={() => setOptimizeImprovement(null)}>
                            {t('transportation.routeBuilder.stops.optimizeNoChange')}
                          </Alert>
                        )
                      )}
                    </Box>
                  )}
                <List dense disablePadding>
                  {selectedStops.map((stop, index) => {
                    const point = pointById.get(stop.collection_point_id);
                    if (!point) return null;
                    return (
                    <ListItem
                      key={`${stop.collection_point_id}-${stop.material_id ?? 'any'}`}
                      divider={index < selectedStops.length - 1}
                      secondaryAction={(
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title={t('transportation.routes.form.moveUp')}>
                            <span>
                              <IconButton size="small" disabled={index === 0} onClick={() => movePoint(index, -1)}>
                                <UpIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('transportation.routes.form.moveDown')}>
                            <span>
                              <IconButton size="small" disabled={index === selectedStops.length - 1} onClick={() => movePoint(index, 1)}>
                                <DownIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('transportation.routes.form.removeStop')}>
                            <IconButton size="small" color="error" onClick={() => removeStopAt(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    >
                      <ListItemText
                        primary={`${index + 1}. ${point.name}`}
                        secondary={(
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.25 }}>
                            {stop.material_id && (
                              <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={materialLabelsById.get(stop.material_id) ?? stop.material_id}
                              />
                            )}
                            {point.city && <Chip size="small" variant="outlined" label={point.city} />}
                            <LabelChips labels={point.labels} emptyFallback="" />
                          </Stack>
                        )}
                      />
                    </ListItem>
                    );
                  })}
                </List>
                </>
              )
            ) : (
              <Box>
                {zoneOptions.length > 0 && (
                  <Box sx={{ p: 2, pb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <LabelIcon fontSize="inherit" /> {t('transportation.routeBuilder.byLabel.title')}
                    </Typography>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      {zoneOptions.map((zone) => (
                        <Chip
                          key={zone.id}
                          size="small"
                          label={zone.name}
                          onClick={() => selectByLabel(zone.id)}
                          sx={{ bgcolor: `${zone.color}22`, color: zone.color, fontWeight: 600, border: '1px solid', borderColor: `${zone.color}55` }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                <Box sx={{ px: 2, pb: 1.5 }}>
                  <Stack spacing={1.5}>
                    <TextField
                      size="small"
                      placeholder={t('suppliers.collectionPoints.list.searchPlaceholder')}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment> }}
                    />
                    <Autocomplete
                      size="small"
                      options={supplierOptions}
                      value={supplierOptions.find((option) => option.id === supplierFilter) ?? null}
                      onChange={(_, value) => setSupplierFilter(value?.id ?? '')}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => <TextField {...params} placeholder={t('suppliers.collectionPoints.list.allSuppliers')} />}
                    />
                    <ToggleButtonGroup size="small" exclusive value={groupBy} onChange={(_, next) => next && setGroupBy(next)}>
                      <ToggleButton value="none">{t('suppliers.collectionPoints.list.groupByNone')}</ToggleButton>
                      <ToggleButton value="city">{t('suppliers.collectionPoints.list.groupByCity')}</ToggleButton>
                      <ToggleButton value="supplier">{t('suppliers.collectionPoints.list.groupBySupplier')}</ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                </Box>

                <Divider />
                <List dense disablePadding>
                  {pointsLoading && (
                    <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>
                  )}
                  {!pointsLoading && groupedAvailable.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{t('transportation.routeBuilder.picker.empty')}</Typography>
                  )}
                  {groupedAvailable.map(({ point, groupLabel }) => (
                    <Box key={point.id}>
                      {groupLabel !== null && (
                        <Box sx={{ px: 2, py: 0.75, bgcolor: 'action.hover' }}>
                          <Typography variant="caption" fontWeight={700}>{groupLabel}</Typography>
                        </Box>
                      )}
                      <ListItemButton onClick={() => addPoint(point.id)}>
                        <ListItemText
                          primary={point.name}
                          secondary={[point.city, supplierNameById.get(point.supplier_id)].filter(Boolean).join(' · ')}
                        />
                        {inheritedMaterialIds(point).length > 1 && (usedMaterialsByPoint.get(point.id)?.size ?? 0) > 0 && (
                          <Chip
                            size="small"
                            variant="outlined"
                            sx={{ mr: 1 }}
                            label={`${usedMaterialsByPoint.get(point.id)?.size ?? 0}/${inheritedMaterialIds(point).length}`}
                          />
                        )}
                        <AddIcon fontSize="small" color="action" />
                      </ListItemButton>
                    </Box>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      <Dialog open={Boolean(materialPicker)} onClose={() => setMaterialPicker(null)} fullWidth maxWidth="xs">
        <DialogTitle>{t('transportation.routeBuilder.picker.chooseMaterial', { name: materialPicker?.point.name ?? '' })}</DialogTitle>
        <List dense disablePadding sx={{ pb: 1 }}>
          {(materialPicker?.options ?? []).map((materialId) => (
            <ListItemButton
              key={materialId}
              onClick={() => {
                if (materialPicker) pushStop(materialPicker.point.id, materialId);
                setMaterialPicker(null);
              }}
            >
              <ListItemText primary={materialLabelsById.get(materialId) ?? materialId} />
              <AddIcon fontSize="small" color="action" />
            </ListItemButton>
          ))}
        </List>
      </Dialog>
    </Box>
  );
}
